// Simplified Telegram service for development without external SDK dependency
// In production, this should use @telegram-apps/sdk
import type { TelegramUser } from '../types';

// Mock Telegram WebApp interface for development
interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      language_code?: string;
    };
    auth_date?: number;
    hash?: string;
  };
  ready: () => void;
  expand: () => void;
  close: () => void;
  MainButton: {
    text: string;
    show: () => void;
    hide: () => void;
    onClick: (callback: () => void) => void;
    setText: (text: string) => void;
  };
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (callback: () => void) => void;
  };
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
  };
  themeParams: {
    bg_color?: string;
    text_color?: string;
    hint_color?: string;
    link_color?: string;
    button_color?: string;
    button_text_color?: string;
    secondary_bg_color?: string;
  };
}

export class TelegramService {
  private static instance: TelegramService;
  private initialized = false;
  private mockWebApp: TelegramWebApp | null = null;

  private constructor() {
    this.initializeMockWebApp();
  }

  public static getInstance(): TelegramService {
    if (!TelegramService.instance) {
      TelegramService.instance = new TelegramService();
    }
    return TelegramService.instance;
  }

  /**
   * Initialize mock WebApp for development
   */
  private initializeMockWebApp(): void {
    // Create mock WebApp for development/testing
    this.mockWebApp = {
      initData: 'user=%7B%22id%22%3A123456789%2C%22first_name%22%3A%22Test%22%2C%22last_name%22%3A%22User%22%2C%22username%22%3A%22testuser%22%2C%22language_code%22%3A%22ru%22%7D&auth_date=1640995200&hash=test_hash',
      initDataUnsafe: {
        user: {
          id: 123456789,
          first_name: 'Test',
          last_name: 'User',
          username: 'testuser',
          language_code: 'ru'
        },
        auth_date: Math.floor(Date.now() / 1000),
        hash: 'test_hash'
      },
      ready: () => console.log('Telegram WebApp ready'),
      expand: () => console.log('Telegram WebApp expanded'),
      close: () => console.log('Telegram WebApp closed'),
      MainButton: {
        text: '',
        show: () => console.log('Main button shown'),
        hide: () => console.log('Main button hidden'),
        onClick: (callback: () => void) => console.log('Main button click handler set'),
        setText: (text: string) => console.log('Main button text set:', text)
      },
      BackButton: {
        show: () => console.log('Back button shown'),
        hide: () => console.log('Back button hidden'),
        onClick: (callback: () => void) => console.log('Back button click handler set')
      },
      HapticFeedback: {
        impactOccurred: (style: string) => console.log('Haptic feedback:', style)
      },
      themeParams: {
        bg_color: '#ffffff',
        text_color: '#000000',
        hint_color: '#999999',
        link_color: '#0088cc',
        button_color: '#0088cc',
        button_text_color: '#ffffff',
        secondary_bg_color: '#f0f0f0'
      }
    };
  }

  /**
   * Initialize Telegram WebApp SDK
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      const webApp = this.getWebApp();
      if (webApp) {
        webApp.ready();
        webApp.expand();
        console.log('Telegram WebApp initialized successfully');
      } else {
        console.log('Using mock Telegram WebApp for development');
      }
      
      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize Telegram WebApp:', error);
      throw error;
    }
  }

  /**
   * Get Telegram WebApp instance (real or mock)
   */
  private getWebApp(): TelegramWebApp | null {
    // In development mode, prefer mock WebApp
    if (import.meta.env.VITE_DEV_MODE === 'true') {
      return this.mockWebApp;
    }
    
    if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
      return window.Telegram.WebApp;
    }
    
    // Fallback to mock for development
    return this.mockWebApp;
  }

  /**
   * Get Telegram user data from initData
   */
  public getTelegramUser(): TelegramUser | null {
    try {
      const webApp = this.getWebApp();
      if (!webApp?.initDataUnsafe?.user) {
        console.warn('No user data available in initData');
        
        // In development mode, return mock user data
        if (import.meta.env.VITE_DEV_MODE === 'true' && this.mockWebApp?.initDataUnsafe?.user) {
          console.log('Using mock user data for development');
          const user = this.mockWebApp.initDataUnsafe.user;
          return {
            id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
            username: user.username,
            language_code: user.language_code,
          };
        }
        
        return null;
      }

      const user = webApp.initDataUnsafe.user;
      return {
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        username: user.username,
        language_code: user.language_code,
      };
    } catch (error) {
      console.error('Error getting Telegram user data:', error);
      return null;
    }
  }

  /**
   * Get the raw initData string for authentication
   */
  public getInitData(): string {
    try {
      const webApp = this.getWebApp();
      const initData = webApp?.initData || '';
      
      // In development mode, return mock initData if real data is not available
      if (!initData && import.meta.env.VITE_DEV_MODE === 'true' && this.mockWebApp?.initData) {
        console.log('Using mock initData for development');
        return this.mockWebApp.initData;
      }
      
      return initData;
    } catch (error) {
      console.error('Error getting initData:', error);
      return '';
    }
  }

  /**
   * Check if running in Telegram environment
   */
  public isTelegramEnvironment(): boolean {
    // In development mode, always return true to allow testing
    if (import.meta.env.VITE_DEV_MODE === 'true') {
      return true;
    }
    
    if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
      return true;
    }
    
    // Fallback to mock for development
    return this.mockWebApp !== null;
  }

  /**
   * Show main button with text and callback
   */
  public showMainButton(text: string, callback: () => void): void {
    try {
      const webApp = this.getWebApp();
      if (webApp?.MainButton) {
        webApp.MainButton.setText(text);
        webApp.MainButton.show();
        webApp.MainButton.onClick(callback);
      }
    } catch (error) {
      console.error('Error showing main button:', error);
    }
  }

  /**
   * Hide main button
   */
  public hideMainButton(): void {
    try {
      const webApp = this.getWebApp();
      if (webApp?.MainButton) {
        webApp.MainButton.hide();
      }
    } catch (error) {
      console.error('Error hiding main button:', error);
    }
  }

  /**
   * Show back button with callback
   */
  public showBackButton(callback: () => void): void {
    try {
      const webApp = this.getWebApp();
      if (webApp?.BackButton) {
        webApp.BackButton.show();
        webApp.BackButton.onClick(callback);
      }
    } catch (error) {
      console.error('Error showing back button:', error);
    }
  }

  /**
   * Hide back button
   */
  public hideBackButton(): void {
    try {
      const webApp = this.getWebApp();
      if (webApp?.BackButton) {
        webApp.BackButton.hide();
      }
    } catch (error) {
      console.error('Error hiding back button:', error);
    }
  }

  /**
   * Trigger haptic feedback
   */
  public hapticFeedback(type: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'light'): void {
    try {
      const webApp = this.getWebApp();
      if (webApp?.HapticFeedback) {
        webApp.HapticFeedback.impactOccurred(type);
      }
    } catch (error) {
      console.error('Error triggering haptic feedback:', error);
    }
  }

  /**
   * Close the mini app
   */
  public close(): void {
    try {
      const webApp = this.getWebApp();
      if (webApp?.close) {
        webApp.close();
      }
    } catch (error) {
      console.error('Error closing mini app:', error);
    }
  }

  /**
   * Get current theme parameters
   */
  public getThemeParams() {
    try {
      const webApp = this.getWebApp();
      return webApp?.themeParams || null;
    } catch (error) {
      console.error('Error getting theme parameters:', error);
      return null;
    }
  }
}

// Global type declaration for Telegram WebApp
declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}